/**
 * Event and expense category definitions
 * Colors mapped to Mytineri brand palette
 */

// Category color mappings to CSS variables
// Category 1 (Blue-grey): Flights/Transport
// Category 2 (Teal): Accommodation  
// Category 3 (Sage): Transit
// Category 4 (Lavender): Activities
// Category 5 (Dusty rose): Food & Drink
// Category 6 (Gold): Miscellaneous/Visas

// Event categories (for calendar)
export const EVENT_CATEGORIES = [
  { value: 'flight', label: 'Flight', icon: 'AirplaneInFlight', colorVar: 1 },
  { value: 'accommodation', label: 'Accommodation', icon: 'Bed', colorVar: 2 },
  { value: 'transit', label: 'Other Transit', icon: 'Bus', colorVar: 3 },
  { value: 'activity', label: 'Activity', icon: 'Park', colorVar: 4 },
];

// Expense/budget categories (ordered A-Z for display, with specific order for budget view)
export const EXPENSE_CATEGORIES = [
  { value: 'accommodation', label: 'Accommodation', icon: 'Bed', colorVar: 2 },
  { value: 'activities', label: 'Activities', icon: 'Park', colorVar: 4 },
  { value: 'food_drink', label: 'Food & Drink', icon: 'BowlSteam', colorVar: 5 },
  { value: 'miscellaneous', label: 'Miscellaneous', icon: 'PuzzlePiece', colorVar: 3 },
  { value: 'transport', label: 'Transport', icon: 'Bus', colorVar: 1 },
  { value: 'visas', label: 'Visas & Fees', icon: 'IdentificationCard', colorVar: 6 },
];

// Budget categories in specific display order
export const BUDGET_CATEGORIES_ORDERED = [
  { value: 'transport', label: 'Transport', icon: 'Bus', colorVar: 1 },
  { value: 'accommodation', label: 'Accommodation', icon: 'Bed', colorVar: 2 },
  { value: 'activities', label: 'Activities', icon: 'Park', colorVar: 4 },
  { value: 'food_drink', label: 'Food & Drink', icon: 'BowlSteam', colorVar: 5 },
  { value: 'visas', label: 'Visas & Fees', icon: 'IdentificationCard', colorVar: 6 },
  { value: 'miscellaneous', label: 'Miscellaneous', icon: 'PuzzlePiece', colorVar: 3 },
];

// Entry types for the unified modal
export const ENTRY_TYPES = [
  { value: 'event', label: 'Event Only', description: 'Add to calendar only' },
  { value: 'expense', label: 'Expense Only', description: 'Track cost without calendar entry' },
  { value: 'both', label: 'Event & Expense', description: 'Add to calendar and track cost' },
];

// Helper functions
export function getEventCategory(value) {
  return EVENT_CATEGORIES.find(c => c.value === value) || EVENT_CATEGORIES[0];
}

export function getExpenseCategory(value) {
  return EXPENSE_CATEGORIES.find(c => c.value === value) || EXPENSE_CATEGORIES[3];
}

// Get category color as CSS variable reference
export function getCategoryColorVar(colorVar) {
  return `var(--color-category-${colorVar})`;
}

// Get category background style (15% opacity background with full color text)
export function getCategoryStyle(colorVar) {
  const baseColors = {
    1: { light: '#3A5F82', dark: '#51789A' },  // Flight/Transport
    2: { light: '#5D9085', dark: '#74A79B' },  // Accommodation
    3: { light: '#7C9981', dark: '#92AF96' },  // Transit
    4: { light: '#8B7FAF', dark: '#A396C4' },  // Activities
    5: { light: '#A88F87', dark: '#BFA8A0' },  // Food & Drink
    6: { light: '#BDA66E', dark: '#D1BE85' },  // Miscellaneous/Visas
  };
  
  const color = baseColors[colorVar] || baseColors[6];
  return {
    backgroundColor: `${color.light}26`, // 15% opacity
    color: `var(--color-category-${colorVar})`,
  };
}

export function getEventCategoryColor(category) {
  const cat = getEventCategory(category);
  return getCategoryColorVar(cat.colorVar);
}

export function getEventCategoryBgLight(category) {
  const cat = getEventCategory(category);
  return getCategoryStyle(cat.colorVar);
}

export function getExpenseCategoryColor(category) {
  const cat = getExpenseCategory(category);
  return getCategoryColorVar(cat.colorVar);
}

export function getExpenseCategoryStyle(category) {
  const cat = getExpenseCategory(category);
  return getCategoryStyle(cat.colorVar);
}

// Currency formatting
export function formatCurrency(amount, currency = 'EUR') {
  return new Intl.NumberFormat('en-IE', {
    style: 'currency',
    currency: currency,
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(amount);
}

// Percentage formatting
export function formatPercent(value) {
  return `${Math.round(value)}%`;
}

// Map event category to expense category (for linked entries)
export function mapEventToExpenseCategory(eventCategory) {
  const mapping = {
    flight: 'transport',
    accommodation: 'accommodation',
    transit: 'transport',
    activity: 'activities',
  };
  return mapping[eventCategory] || 'miscellaneous';
}
